import { EntityBase } from '@provision/entity-types';
import { useMemo } from 'react';
import { isNullish } from 'remeda';
import { Client } from 'src/graphql';
import { Address } from 'src/graphql/entities/address';
import { Conversation } from 'src/graphql/entities/conversation';
import {
  CustomViewAttribute,
  CustomViewFormConditionField,
  CustomViewSorting,
} from 'src/graphql/entities/custom-view/custom-view.types';
import { useGroupRules } from 'src/graphql/entities/group-rules/group-rules';
import {
  PartnerGroupRules,
  UserGroupRules,
} from 'src/graphql/entities/group-rules/group-rules.types';
import { Project } from 'src/graphql/entities/project';
import { SearchQueryByEntityType } from 'src/graphql/entities/search/use-search';
import { ConditionInput, EntityNames } from 'src/graphql/types';
import { useGlobalStore } from 'src/store';

import { CacheManager } from 'src/utils/hooks/use-cache-manager';
import { filterAiSiteSummaryAttributes } from './ai-site-summary-access';
import {
  CLIENT_CUSTOM_FILTER_MAPPER,
  clientAttributeConfigList,
  clientAttributeConfigMap,
  clientFilterAttributesList,
  clientFilterAttributesMap,
  useClientRecords,
} from './client';
import {
  CONVERSATION_FILTER_MAPPER,
  conversationAttributeConfigList,
  conversationAttributeConfigMap,
  conversationFilterAttributesList,
  conversationFilterAttributesMap,
  useConversationRecords,
} from './conversation';
import {
  useCustomViewCustomAttributes,
  withCustomAttributes,
} from './custom-attributes-bridge';
import {
  PROJECT_FILTER_MAPPER,
  projectAttributeConfigList,
  projectAttributeConfigMap,
  projectFilterAttributesList,
  projectFilterAttributesMap,
  useProjectRecords,
} from './project';
import { filterServiceLevelAttributes } from './service-level-access';
import {
  SITE_CUSTOM_FILTER_MAPPER,
  siteAttributeConfigList,
  siteAttributeConfigMap,
  siteFilterAttributesList,
  siteFilterAttributesMap,
  useSiteRecords,
} from './site';

interface CustomViewEntityConfig<T = EntityBase> {
  attributesMap: Map<string, CustomViewAttribute>;
  filterAttributesMap: Map<string, CustomViewFormConditionField>;
  filterAttributesList: CustomViewFormConditionField[];
  attributesList: CustomViewAttribute[];
  useViewEntities: (data: {
    filters: ConditionInput[];
    limit: number;
    sort?: CustomViewSorting | null;
    searchTerm?: string;
    dynamicAttributePaths?: string[];
  }) => {
    refetch: (data: unknown) => void;
    records: T[];
    isLoading: boolean;
    isLoadingMore: boolean;
    totalRecords: number;
    hasMore: boolean;
    loadMore: (limit?: number, offset?: number) => Promise<void>;
    cacheManager: CacheManager<SearchQueryByEntityType<T>>;
  };
  isSearchable: boolean;
}

export type AnyCustomViewEntity = Conversation | Address | Client | Project;

export const CUSTOM_VIEWS_CONFIG: Record<
  EntityNames.Conversation | EntityNames.Project | EntityNames.Site | EntityNames.Client,
  CustomViewEntityConfig<AnyCustomViewEntity>
> = {
  [EntityNames.Conversation]: {
    filterAttributesMap: conversationFilterAttributesMap,
    filterAttributesList: conversationFilterAttributesList,
    attributesMap: conversationAttributeConfigMap,
    attributesList: conversationAttributeConfigList,
    useViewEntities: useConversationRecords,
    isSearchable: true,
  } as CustomViewEntityConfig<Conversation>,
  [EntityNames.Site]: {
    filterAttributesMap: siteFilterAttributesMap,
    filterAttributesList: siteFilterAttributesList,
    attributesMap: siteAttributeConfigMap,
    attributesList: siteAttributeConfigList,
    useViewEntities: useSiteRecords,
    isSearchable: true,
  } as CustomViewEntityConfig<Address>,
  [EntityNames.Client]: {
    filterAttributesMap: clientFilterAttributesMap,
    filterAttributesList: clientFilterAttributesList,
    attributesMap: clientAttributeConfigMap,
    attributesList: clientAttributeConfigList,
    useViewEntities: useClientRecords,
    isSearchable: true,
  } as CustomViewEntityConfig<Client>,
  [EntityNames.Project]: {
    filterAttributesMap: projectFilterAttributesMap,
    filterAttributesList: projectFilterAttributesList,
    attributesMap: projectAttributeConfigMap,
    attributesList: projectAttributeConfigList,
    useViewEntities: useProjectRecords,
    isSearchable: true,
  } as CustomViewEntityConfig<Project>,
} as const;

export type CustomViewEntity = keyof typeof CUSTOM_VIEWS_CONFIG;

export const useCustomViewEntityConfig = <T extends CustomViewEntity>(entityName: T) => {
  const config = CUSTOM_VIEWS_CONFIG[entityName] ?? CUSTOM_VIEWS_CONFIG.conversation;
  const { groupRules, checkValidity } = useGroupRules();
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const customAttributes = useCustomViewCustomAttributes(entityName);

  const hasServiceLevelAccess =
    isNullish(selectedPartnerId) ||
    Boolean(groupRules[PartnerGroupRules.AccessToPlatformSLAs]);
  const hasAiSiteSummaryAccess = checkValidity([UserGroupRules.AccessToAiSiteSummary]);

  return useMemo(
    () => ({
      ...withCustomAttributes(
        filterAiSiteSummaryAttributes(
          filterServiceLevelAttributes(config, hasServiceLevelAccess),
          hasAiSiteSummaryAccess,
        ),
        customAttributes,
      ),
      // surfaced here so the grid table has a single catalog call site (the config hook owns it)
      columnRenderersById: customAttributes.columnRenderersById,
    }),
    [config, hasServiceLevelAccess, hasAiSiteSummaryAccess, customAttributes],
  );
};

// object based approach instead of field based approach is because of
// field accessability purposes during conditions mapping
export const CUSTOM_FILTER_MAPPER = {
  conversation: CONVERSATION_FILTER_MAPPER,
  client: CLIENT_CUSTOM_FILTER_MAPPER,
  site: SITE_CUSTOM_FILTER_MAPPER,
  project: PROJECT_FILTER_MAPPER,
} as const;
